import { LevelDesignerComponent } from '../level-designer/level-designer.component';
import { LevelDesignerElementBase } from '../elements/element-base';
import { FormControl, AbstractControl, FormGroup, FormArray } from '@angular/forms';
import { PropertyMetadata, PropertyInputType } from './property-types';
import { OrganizationsService } from 'src/app/Services/organizations.service';
import { ApiService } from 'src/app/Services/api.service';
import { MultipleValues, PropertyInput } from './inputs/input-base';
import { TextInput } from './inputs/text-input';
import { NumberInput } from './inputs/number-input';
import { DimensionInput } from './inputs/dimension-input';
import { SelectInput } from './inputs/select-input';
import { ApiSelectInput } from './inputs/api-select-input';
import { SearchableSelectInput } from './inputs/searchable-select-input';
import { FormControlSelectInput } from './inputs/form-control-select-input';
import { ColorInput } from './inputs/color-input';
import { AngleInput } from './inputs/angle-input';
import { CheckboxInput } from './inputs/checkbox-input';
import { EmbossedRectangle } from '../utils/embossed-rectangle';
import { MouseEventResult } from '../managers/manager-base';

interface PropertyDefinition {
    name: string;
    displayName: string;
    type: 'string' | 'number' | 'boolean' | 'color';
    formControlName: string;
}

interface ActiveInput {
    row: number;
    value: string;
    cursorPosition: number;
    isSelected?: boolean;  // Track if text is selected
}

export class PropertyEditor {
    private ctx: CanvasRenderingContext2D;
    private width: number = 250;
    private height: number;
    private x: number;
    private y: number = 60; // Below toolbar
    private selectedElements: LevelDesignerElementBase[] = [];
    private commonProperties: PropertyMetadata[] = [];
    private rowHeight: number = 25;
    private headerHeight: number = 40; // Match toolbar height
    private nameColumnWidth: number = 120;
    private editingProperty: { row: number, value: string } | null = null;
    private activeInput: PropertyInput | null = null;
    private activeRow: number = -1;
    private selectOptions: Map<number, any[]> = new Map(); // Cache for API results
    private searchTimeout: any;
    private dropdownVisible: boolean = false;
    private dropdownRow: number | null = null;
    private highlightedOption: number = -1;
    private inputs: PropertyInput[] = [];
    private expandedCategories: Set<string> = new Set();
    private categorizedProperties: Map<string, PropertyMetadata[]> = new Map();
    private isCollapsed: boolean = false;
    private animationStartTime: number | null = null;
    private animationProgress: number = 1; // 0 to 1
    private targetHeight: number = 0;

    constructor(
        private levelDesigner: LevelDesignerComponent, 
        private canvas: HTMLCanvasElement,
        private organizationsService: OrganizationsService,
        private apiService: ApiService
    ) {
        this.ctx = canvas.getContext('2d')!;
        this.height = canvas.height - this.y;
        this.targetHeight = this.height;
        this.x = canvas.width - this.width - 10;

        this.levelDesigner.selectionChanged.subscribe(elements => {
            if (this.activeInput) {
                this.unfocusActiveInput();
            }
            this.selectedElements = elements;
            this.updateCommonProperties();
            // Expand General category by default
            this.expandedCategories.add('General');
            this.levelDesigner.redraw();
        });
    }

    private updateCommonProperties() {
        if (this.selectedElements.length === 0) {
            this.commonProperties = [];
            this.inputs = [];
            this.categorizedProperties.clear();
            return;
        }

        // Get metadata from first element
        const firstElement = this.selectedElements[0];
        const allProperties = firstElement.getPropertyMetadata();

        // Helper function to check if a nested form control exists
        const formControlExists = (formGroup: FormGroup, path: string): boolean => {
            const segments = path.split('.');
            let current: any = formGroup;

            for (const segment of segments) {
                if (current instanceof FormGroup) {
                    current = current.get(segment);
                } else if (current instanceof FormArray) {
                    const index = parseInt(segment);
                    if (!isNaN(index)) {
                        current = current.at(index);
                    }
                } else {
                    return false;
                }
                if (!current) return false;
            }
            return true;
        };

        // Filter to properties that exist in all selected elements and respect hideOnMultipleSelection flag
        this.commonProperties = allProperties.filter(prop => {
            // Skip properties that should be hidden when multiple elements are selected
            if (this.selectedElements.length > 1 && prop.hideOnMultipleSelection) {
                return false;
            }

            return this.selectedElements.every(element => {
                if (prop.formControlName) {
                    return formControlExists(element.formGroup, prop.formControlName) &&
                        element.getPropertyMetadata().some(p => p.formControlName === prop.formControlName);
                }
                return element.getPropertyMetadata().some(p => p.displayName === prop.displayName);
            });
        }).sort((a, b) => (a.order || 0) - (b.order || 0));

        // Group properties by category
        const categorizedProperties = new Map<string, PropertyMetadata[]>();
        this.commonProperties.forEach(prop => {
            const category = prop.category || 'General';
            if (!categorizedProperties.has(category)) {
                categorizedProperties.set(category, []);
            }
            categorizedProperties.get(category)!.push(prop);
        });
        
        // Create a flat ordered list of properties based on category grouping
        const orderedProperties: PropertyMetadata[] = [];
        const categoryOrder = ['General', 'Dimensions', 'Appearance'];
        
        // Add properties from known categories in a specific order
        categoryOrder.forEach(category => {
            if (categorizedProperties.has(category)) {
                orderedProperties.push(...categorizedProperties.get(category)!);
                categorizedProperties.delete(category);
            }
        });
        
        // Add any remaining categories in the order they appear
        categorizedProperties.forEach((properties, category) => {
            orderedProperties.push(...properties);
        });
        
        // Replace the commonProperties with the ordered list
        this.commonProperties = orderedProperties;

        // Initialize all inputs
        this.inputs = this.commonProperties.map((prop, index) => {
            const value = this.getCommonValue(prop);
            return this.createInput(prop, index, value, false);
        });

        // Update categorized properties
        this.categorizedProperties.clear();
        this.commonProperties.forEach(prop => {
            const category = prop.category || 'General';
            if (!this.categorizedProperties.has(category)) {
                this.categorizedProperties.set(category, []);
                // Expand all categories by default
                this.expandedCategories.add(category);
            }
            this.categorizedProperties.get(category)!.push(prop);
        });
    }

    private formatDisplayName(controlName: string): string {
        // Convert camelCase to Title Case with spaces
        return controlName
            // Insert a space before all caps
            .replace(/([A-Z])/g, ' $1')
            // Uppercase the first character
            .replace(/^./, str => str.toUpperCase())
            .trim();
    }

    private getPropertyType(control: AbstractControl | null): 'string' | 'number' | 'boolean' | 'color' {
        if (!control) return 'string';

        const value = control.value;
        if (typeof value === 'number') return 'number';
        if (typeof value === 'boolean') return 'boolean';
        if (typeof value === 'string' && value.match(/^#[0-9A-Fa-f]{6}$/)) return 'color';
        return 'string';
    }

    private createInput(prop: PropertyMetadata, row: number, value: any, isActive: boolean): PropertyInput {
        const x = this.x + this.nameColumnWidth + 5;
        const y = this.y + this.rowHeight;
        const width = this.width - this.nameColumnWidth - 10;
        const height = this.rowHeight - 4;

        if (prop.inputType.type === 'form-control-select') {
            const formControlType = prop.inputType;
            const parentElement = this.selectedElements[0].currentParentElement;
            if (!parentElement) {
                return new TextInput(this.ctx, x, y, width, height, value, isActive, this.levelDesigner, this);
            }

            return new FormControlSelectInput(
                this.ctx,
                x,
                y,
                width,
                height,
                value,
                isActive,
                this.levelDesigner,
                this,
                formControlType.formArrayName,
                formControlType.idProperty,
                formControlType.labelProperty
            );
        }

        switch (prop.inputType.type) {
            case 'text':
                return new TextInput(this.ctx, x, y, width, height, value, isActive, this.levelDesigner, this);
            case 'number':
                const numType = prop.inputType;
                return new NumberInput(this.ctx, x, y, width, height, value, isActive, this.levelDesigner, this,
                    numType.min, numType.max);
            case 'dimension':
                return new DimensionInput(this.ctx, x, y, width, height, value, isActive, this.organizationsService, this.levelDesigner, this);
            case 'select':
                const selectType = prop.inputType;
                return new SelectInput(this.ctx, x, y, width, height, value, isActive, selectType.options, this.levelDesigner, this);
            case 'api-select':
                const apiType = prop.inputType;
                return new ApiSelectInput(this.ctx, x, y, width, height, value, isActive, this.apiService, apiType.url, apiType.idProperty, apiType.labelProperty, this.levelDesigner, this, this.organizationsService, apiType.includeNoneOption ?? false);
            case 'searchable-select':
                const searchType = prop.inputType;
                let displayValue: string | undefined;
                
                // If this is an Id field, try to find the corresponding Name field
                if (prop.formControlName && prop.formControlName.endsWith('Id')) {
                    const nameField = prop.formControlName.slice(0, -2) + 'Name';
                    // Check if any selected element has this name field in their form
                    for (const element of this.selectedElements) {
                        const nameControl = element.formGroup.get(nameField);
                        if (nameControl) {
                            displayValue = nameControl.value;
                            break;
                        }
                    }
                }

                const input = new SearchableSelectInput(
                    this.ctx,
                    x,
                    y,
                    width,
                    height,
                    value,
                    isActive,
                    this.apiService,
                    searchType.url,
                    searchType.idProperty,
                    searchType.labelProperty,
                    searchType.minSearchLength || 2,
                    this.levelDesigner,
                    this,
                    this.organizationsService,
                    displayValue
                );

                // Set the initial display value if we found one
                if (displayValue) {
                    input.setInitialDisplayValue(displayValue);
                }

                return input;
            case 'color':
                return new ColorInput(this.ctx, x, y, width, height, value, isActive, this.levelDesigner, this);
            case 'angle':
                return new AngleInput(this.ctx, x, y, width, height, value, isActive, this.levelDesigner, this);
            case 'boolean':
                return new CheckboxInput(this.ctx, x, y, width, height, value, isActive, this.levelDesigner, this);
            default:
                return new TextInput(this.ctx, x, y, width, height, value, isActive, this.levelDesigner, this);
        }
    }

    private drawTreeNode(x: number, y: number, isExpanded: boolean) {
        // Draw expand/collapse box
        this.ctx.strokeStyle = '#666666';
        this.ctx.lineWidth = 1;
        this.ctx.beginPath();
        this.ctx.rect(x, y - 6, 9, 9);
        this.ctx.stroke();

        // Draw +/- symbol
        this.ctx.beginPath();
        this.ctx.moveTo(x + 2, y - 1.5);
        this.ctx.lineTo(x + 7, y - 1.5);
        this.ctx.stroke();

        if (!isExpanded) {
            // Draw vertical line for + symbol
            this.ctx.beginPath();
            this.ctx.moveTo(x + 4.5, y - 4);
            this.ctx.lineTo(x + 4.5, y + 1);
            this.ctx.stroke();
        }
    }

    private drawTreeLines(x: number, y: number, height: number) {
        this.ctx.strokeStyle = '#cccccc';
        this.ctx.lineWidth = 1;
        this.ctx.beginPath();
        this.ctx.moveTo(x, y);
        this.ctx.lineTo(x, y + height);
        this.ctx.stroke();
    }

    public draw() {
        // Calculate current height based on animation progress
        const fullHeight = this.canvas.height - this.y;
        const collapsedHeight = this.headerHeight;
        this.height = collapsedHeight + (fullHeight - collapsedHeight) * this.animationProgress;

        this.ctx.save();
        
        // Create clipping region with rounded corners
        this.ctx.beginPath();
        const radius = 8;
        if (this.isCollapsed) {
            // All corners rounded when collapsed
            this.roundRect(this.x, this.y, this.width, this.height, radius);
        } else {
            // Only top corners rounded when expanded
            this.ctx.moveTo(this.x + radius, this.y);
            this.ctx.lineTo(this.x + this.width - radius, this.y);
            this.ctx.quadraticCurveTo(this.x + this.width, this.y, this.x + this.width, this.y + radius);
            this.ctx.lineTo(this.x + this.width, this.y + this.height);
            this.ctx.lineTo(this.x, this.y + this.height);
            this.ctx.lineTo(this.x, this.y + radius);
            this.ctx.quadraticCurveTo(this.x, this.y, this.x + radius, this.y);
        }
        this.ctx.clip();

        // Create gradient for header background
        const gradient = this.ctx.createLinearGradient(this.x, this.y, this.x, this.y + this.headerHeight);
        gradient.addColorStop(0, '#E8E8E8');
        gradient.addColorStop(1, '#C8C8C8');

        // Draw header with gradient
        EmbossedRectangle.draw(this.ctx, this.x, this.y, this.width, this.headerHeight, {
            topLeft: 5,
            topRight: 5,
            bottomRight: this.isCollapsed ? 5 : 0,
            bottomLeft: this.isCollapsed ? 5 : 0
        }, gradient);

        // Add highlight for embossed effect
        this.ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
        this.ctx.lineWidth = 2;
        this.ctx.beginPath();
        this.ctx.moveTo(this.x + radius, this.y + 1);
        this.ctx.lineTo(this.x + this.width - radius, this.y + 1);
        this.ctx.stroke();

        // Add shadow for embossed effect
        this.ctx.strokeStyle = 'rgba(0, 0, 0, 0.2)';
        this.ctx.beginPath();
        if (this.isCollapsed) {
            // When collapsed, shadow follows rounded corners
            this.ctx.moveTo(this.x + this.width - radius, this.y + this.headerHeight);
            this.ctx.arcTo(this.x + this.width, this.y + this.headerHeight, this.x + this.width, this.y + this.headerHeight - radius, radius);
            this.ctx.arcTo(this.x + this.width, this.y + this.headerHeight, this.x, this.y + this.headerHeight, radius);
            this.ctx.arcTo(this.x, this.y + this.headerHeight, this.x, this.y + this.headerHeight - radius, radius);
        } else {
            // When expanded, shadow goes edge to edge
            this.ctx.moveTo(this.x, this.y + this.headerHeight);
            this.ctx.lineTo(this.x + this.width, this.y + this.headerHeight);
        }
        this.ctx.stroke();

        // Draw header text
        this.ctx.fillStyle = '#333333';
        this.ctx.font = 'bold 12px Arial';
        this.ctx.textAlign = 'left';
        this.ctx.textBaseline = 'middle';
        this.ctx.fillText(this.getHeaderText(), this.x + 10, this.y + this.headerHeight / 2);

        // Draw chevron
        const chevronX = this.x + this.width - 25;
        const chevronY = this.y + this.headerHeight / 2;
        const chevronSize = 8;
        
        this.ctx.beginPath();
        this.ctx.strokeStyle = '#666666';
        this.ctx.lineWidth = 2;
        
        if (this.isCollapsed) {
            // Draw chevron down
            this.ctx.moveTo(chevronX - chevronSize/2, chevronY - chevronSize/3);
            this.ctx.lineTo(chevronX, chevronY + chevronSize/3);
            this.ctx.lineTo(chevronX + chevronSize/2, chevronY - chevronSize/3);
        } else {
            // Draw chevron up
            this.ctx.moveTo(chevronX - chevronSize/2, chevronY + chevronSize/3);
            this.ctx.lineTo(chevronX, chevronY - chevronSize/3);
            this.ctx.lineTo(chevronX + chevronSize/2, chevronY + chevronSize/3);
        }
        this.ctx.stroke();

        // If collapsed or animating to collapsed, don't draw the rest
        if (this.animationProgress === 0) {
            this.ctx.restore();
            return;
        }

        // Draw content with opacity based on animation progress
        this.ctx.globalAlpha = this.animationProgress;

        // Draw white background for content area
        this.ctx.fillStyle = '#ffffff';
        this.ctx.fillRect(this.x, this.y + this.headerHeight, this.width, this.height - this.headerHeight);

        // Draw table grid
        const startY = this.y + this.headerHeight + this.rowHeight;
        
        let currentY = startY;
        let rowIndex = 0;
        let inputIndex = 0;
        const inputPositions: {input: PropertyInput, y: number}[] = [];

        // Draw each category and its properties
        this.categorizedProperties.forEach((properties, category) => {
            const isExpanded = this.expandedCategories.has(category);
            
            // Draw category header with tree node
            this.ctx.fillStyle = '#ffffff'; // Changed to white background
            this.ctx.fillRect(this.x, currentY, this.width, this.rowHeight);
            
            // Draw tree node (expand/collapse indicator)
            this.drawTreeNode(this.x + 5, currentY + this.rowHeight / 2, isExpanded);
            
            // Draw category name
            this.ctx.fillStyle = '#333333';
            this.ctx.font = 'bold 12px Arial';
            this.ctx.textAlign = 'left';
            this.ctx.textBaseline = 'middle';
            this.ctx.fillText(category, this.x + 20, currentY + this.rowHeight / 2);
            
            currentY += this.rowHeight;

            if (isExpanded) {
                // Draw vertical tree line for the category
                const categoryStartY = currentY;
                const categoryHeight = properties.length * this.rowHeight;
                this.drawTreeLines(this.x + 12, categoryStartY, categoryHeight - 5);

                // Draw properties in this category
                properties.forEach(prop => {
                    // Draw white background for all rows
                    this.ctx.fillStyle = '#ffffff';
                    this.ctx.fillRect(this.x, currentY, this.width, this.rowHeight);

                    // Draw horizontal tree line
                    this.ctx.strokeStyle = '#cccccc';
                    this.ctx.beginPath();
                    this.ctx.moveTo(this.x + 12, currentY + this.rowHeight / 2);
                    this.ctx.lineTo(this.x + 20, currentY + this.rowHeight / 2);
                    this.ctx.stroke();

                    // Draw property name with indent
                    const labelX = this.x + 25; // Indented
                    const labelY = currentY + this.rowHeight / 2;
                    this.ctx.fillStyle = '#000000';
                    this.ctx.font = '11px Arial';
                    this.ctx.textAlign = 'left';
                    this.ctx.textBaseline = 'middle';
                    this.ctx.fillText(prop.displayName, labelX, labelY);

                    // Handle input
                    if (inputIndex < this.inputs.length) {
                        const input = this.inputs[inputIndex];
                        input.metadata = prop;
                        inputPositions.push({input, y: currentY + 2});
                        inputIndex++;
                    }

                    currentY += this.rowHeight;
                    rowIndex++;
                });

                // Add spacing between categories
                currentY += 5;
            }
        });
        
        // Draw inputs
        inputPositions.forEach(({input, y}) => {
            input.y = y;
            const inputAsAny = input as any;
            if (!inputAsAny.dropdownVisible) {
                input.draw();
            }
        });
        
        // Draw dropdown inputs last
        inputPositions.forEach(({input, y}) => {
            input.y = y;
            const inputAsAny = input as any;
            if (inputAsAny.dropdownVisible) {
                input.draw();
            }
        });

        this.ctx.globalAlpha = 1;
        this.ctx.restore();
    }

    private drawHeaders() {
        this.ctx.save();
        
        // Draw main header
        this.ctx.fillStyle = '#e0e0e0';
        this.ctx.fillRect(this.x, this.y, this.width, this.headerHeight);
        this.ctx.fillStyle = '#000000';
        this.ctx.font = 'bold 12px Arial';
        this.ctx.textAlign = 'left';
        this.ctx.textBaseline = 'middle';
        this.ctx.fillText(this.getTypeName(), this.x + 10, this.y + this.headerHeight / 2);

        // Draw chevron
        const chevronX = this.x + this.width - 25;
        const chevronY = this.y + this.headerHeight / 2;
        const chevronSize = 8;
        
        this.ctx.beginPath();
        this.ctx.strokeStyle = '#666666';
        this.ctx.lineWidth = 2;
        
        if (this.isCollapsed) {
            // Draw chevron down
            this.ctx.moveTo(chevronX - chevronSize/2, chevronY - chevronSize/3);
            this.ctx.lineTo(chevronX, chevronY + chevronSize/3);
            this.ctx.lineTo(chevronX + chevronSize/2, chevronY - chevronSize/3);
        } else {
            // Draw chevron up
            this.ctx.moveTo(chevronX - chevronSize/2, chevronY + chevronSize/3);
            this.ctx.lineTo(chevronX, chevronY - chevronSize/3);
            this.ctx.lineTo(chevronX + chevronSize/2, chevronY + chevronSize/3);
        }
        this.ctx.stroke();

        // Only draw column headers if not collapsed
        if (this.animationProgress > 0) {
            // Draw column headers
            const columnY = this.y + this.headerHeight;
            this.ctx.fillStyle = '#e8e8e8';
            this.ctx.fillRect(this.x, columnY, this.nameColumnWidth, this.rowHeight);
            this.ctx.fillRect(this.x + this.nameColumnWidth, columnY, this.width - this.nameColumnWidth, this.rowHeight);
            
            this.ctx.fillStyle = '#000000';
            this.ctx.font = '11px Arial';
            this.ctx.fillText('Property', this.x + 5, columnY + this.rowHeight / 2);
            this.ctx.fillText('Value', this.x + this.nameColumnWidth + 5, columnY + this.rowHeight / 2);
        }
        
        this.ctx.restore();
    }

    private getTypeName(): string {
        if (!this.selectedElements || this.selectedElements.length === 0) {
            return '';
        }
        const firstType = this.selectedElements[0].TypeName;
        const allSameType = this.selectedElements.every(element => 
            element.TypeName === firstType
        );
        if (allSameType) {
            if(this.selectedElements.length > 1) {
                return firstType + " (" + this.selectedElements.length + ")";
            }
            return firstType;
        }
        return '<Multiple Types>';
    }

    private drawPropertyName(prop: PropertyMetadata, index: number) {
        const x = this.x + 5;
        const y = this.y + this.headerHeight + ((index + 2) * this.rowHeight) + this.rowHeight / 2;
        this.ctx.fillStyle = '#000000';
        this.ctx.font = '11px Arial';
        this.ctx.textAlign = 'left';
        this.ctx.textBaseline = 'middle';
        this.ctx.fillText(prop.displayName, x, y);
    }

    private getFormControlValue(element: LevelDesignerElementBase, path: string): any {
        // Split the path into segments (e.g., "PortAssignments.0.ParkingSpaceId" -> ["PortAssignments", "0", "ParkingSpaceId"])
        const segments = path.split('.');
        let current: any = element.formGroup;

        for (const segment of segments) {
            if (current instanceof FormGroup) {
                current = current.get(segment);
            } else if (current instanceof FormArray) {
                const index = parseInt(segment);
                if (!isNaN(index)) {
                    current = current.at(index);
                }
            } else {
                return undefined;
            }
        }

        return current?.value;
    }

    private setFormControlValue(element: LevelDesignerElementBase, path: string, value: any): void {
        // Split the path into segments
        const segments = path.split('.');
        let current: any = element.formGroup;
        
        // Navigate to the parent control
        for (let i = 0; i < segments.length - 1; i++) {
            const segment = segments[i];
            if (current instanceof FormGroup) {
                current = current.get(segment);
            } else if (current instanceof FormArray) {
                const index = parseInt(segment);
                if (!isNaN(index)) {
                    current = current.at(index);
                }
            } else {
                return;
            }
        }

        // Set the value on the last segment
        if (current instanceof FormGroup) {
            const control = current.get(segments[segments.length - 1]);
            if (control) {
                control.setValue(value);
                control.markAsDirty();
            }
        }
        // Also update direct property if it exists on the element
        const lastSegment = segments[segments.length - 1];
        if (lastSegment in element) {
            (element as any)[lastSegment] = value;
        }
    }

    private getCommonValue(prop: PropertyMetadata): any {
        const values = this.selectedElements.map(element => {
            if (prop.formControlName) {
                if (prop.getValue) {
                    return prop.getValue(element);
                } else {
                    return this.getFormControlValue(element, prop.formControlName);
                }
            } else if (prop.getValue) {
                return prop.getValue(element);
            }
            return '';
        });

        if (values.length === 0) return '';

        // For dimension inputs, compare with tolerance
        if (prop.inputType.type === 'dimension') {
            const tolerance = 0.0001; // Small enough to catch floating point differences
            const firstValue = parseFloat(values[0]);
            const allSameValue = values.every(v => Math.abs(parseFloat(v) - firstValue) < tolerance);
            return allSameValue ? values[0] : new MultipleValues();
        }

        // For other types, use exact comparison
        const uniqueValues = new Set(values);
        return uniqueValues.size === 1 ? values[0] : new MultipleValues();
    }

    private unfocusActiveInput() {
        if (this.activeInput) {
            this.activeInput.blur();
            this.activeInput = null;
            this.activeRow = -1;
            this.levelDesigner.redraw();
        }
    }

    public handleClick(event: MouseEvent): boolean {
        const rect = this.canvas.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;

        if (!this.isPointInside(x, y)) {
            if (this.activeInput) {
                this.unfocusActiveInput();
            }
            return false;
        }

        // Check if click is in the header area
        if (y >= this.y && y <= this.y + this.headerHeight) {
            // Check if click is on the chevron
            const chevronX = this.x + this.width - 25;
            const chevronY = this.y + this.headerHeight / 2;
            const chevronSize = 8;
            
            if (Math.abs(x - chevronX) <= chevronSize && Math.abs(y - chevronY) <= chevronSize) {
                this.toggleCollapse();
                return true;
            }
        }

        // If collapsed, don't handle any other clicks
        if (this.isCollapsed || this.animationProgress === 0) {
            return true;
        }

        // If we have an active input, let it handle the click first
        if (this.activeInput) {
            const handled = this.activeInput.handleClick(x, y);
            if (handled) {
                this.levelDesigner.redraw();
                return true;
            }
        }

        // Check if click is on a category expand/collapse button
        const startY = this.y + this.headerHeight + this.rowHeight;
        let currentY = startY;
        
        for (const [category, properties] of this.categorizedProperties) {
            // Check if click is in the expand/collapse button area
            if (x >= this.x + 5 && x <= this.x + 14 &&
                y >= currentY && y <= currentY + this.rowHeight) {
                if (this.expandedCategories.has(category)) {
                    this.expandedCategories.delete(category);
                } else {
                    this.expandedCategories.add(category);
                }
                this.levelDesigner.redraw();
                return true;
            }
            
            currentY += this.rowHeight;
            if (this.expandedCategories.has(category)) {
                currentY += properties.length * this.rowHeight + 5;
            }
        }

        // Reset currentY for input click detection
        currentY = startY;
        let clickedInputIndex = -1;

        // Calculate which input was clicked
        for (const [category, properties] of this.categorizedProperties) {
            // Skip if click is above this category
            if (y < currentY) break;
            
            // Move past category header
            currentY += this.rowHeight;

            if (this.expandedCategories.has(category)) {
                for (const prop of properties) {
                    // Check if click is in this property's row
                    if (y >= currentY && y < currentY + this.rowHeight) {
                        // Only handle click if it's in the input area (right side of the property editor)
                        if (x >= this.x + this.nameColumnWidth) {
                            clickedInputIndex = this.inputs.indexOf(this.inputs.find(i => i.metadata === prop)!);
                        }
                        break;
                    }
                    currentY += this.rowHeight;
                }
                
                if (clickedInputIndex !== -1) break;
                currentY += 5; // Category spacing
            }
        }

         //If we found a clicked input, handle it
         if (clickedInputIndex !== -1 && clickedInputIndex < this.inputs.length) {
             // If we have an active input, blur it first
             if (this.activeInput) {
                 this.activeInput.blur();
             }

             this.activeRow = clickedInputIndex;
             this.activeInput = this.inputs[clickedInputIndex];
             const handled = this.activeInput.handleClick(x, y);
            
             // Always redraw after clicking an input
             this.levelDesigner.redraw();
             return true;
         }

        this.unfocusActiveInput();
        return false;
    }

    public handleKeyEvent(event: KeyboardEvent): boolean {
        if (!this.activeInput) return false;

        switch (event.key) {
            case 'Enter':
                this.unfocusActiveInput();
                return true;
            case 'Escape':
                this.activeInput = null;
                this.activeRow = -1;
                this.levelDesigner.redraw();
                return true;
            case 'Tab':
                event.preventDefault();
                const nextRow = event.shiftKey ? 
                    this.activeRow - 1 : 
                    this.activeRow + 1;

                // Commit current edit
                this.inputs[this.activeRow].isActive = false;

                // Move to next/previous input or wrap around
                if (nextRow >= this.inputs.length) {
                    this.activeRow = 0;
                } else if (nextRow < 0) {
                    this.activeRow = this.inputs.length - 1;
                } else {
                    this.activeRow = nextRow;
                }

                // Activate new input
                this.activeInput = this.inputs[this.activeRow];
                this.activeInput.focus();
                this.levelDesigner.redraw();
                return true;
            default:
                const handled = this.activeInput.handleKeyEvent(event);
                if (handled) {
                    this.levelDesigner.redraw();
                }
                return handled;
        }
    }

    private validateInput(value: string, prop: PropertyMetadata): boolean {
        if (prop.inputType.type === 'number' || prop.inputType.type === 'dimension') {
            const num = parseFloat(value);
            if (isNaN(num)) return false;
            
            if (prop.inputType.type === 'number') {
                const { min, max } = prop.inputType;
                if (min !== undefined && num < min) return false;
                if (max !== undefined && num > max) return false;
            }
            
            // Don't allow negative dimensions
            if (prop.inputType.type === 'dimension' && num <= 0) return false;
        }
        return true;
    }

    private formatNumber(value: number, decimals: number = 2): string {
        return Number(value.toFixed(decimals)).toString();
    }

    private formatValueForDisplay(prop: PropertyMetadata, value: string): string {
        switch (prop.inputType.type) {
            case 'boolean':
                return value === 'true' ? '☒' : '☐';
            case 'dimension':
                const num = parseFloat(value);
                if (isNaN(num)) return value;
                const displayValue = this.convertToDisplayValue(num, 'dimension');
                return this.formatNumber(displayValue);
            case 'angle':
                const radians = parseFloat(value);
                if (isNaN(radians)) return value;
                const degrees = (radians * 180) / Math.PI;
                return this.formatNumber(degrees) + '°';
            default:
                return value;
        }
    }

    public isPointInside(x: number, y: number): boolean {
        return x >= this.x && x <= this.x + this.width &&
               y >= this.y && y <= this.y + this.height;
    }

    public isEditing(): boolean {
        return this.activeInput !== null;
    }

    // Optional: Add conversion helpers if needed
    private convertToDisplayValue(value: number, type: 'dimension'): number {
        if (type === 'dimension' && this.organizationsService.MeasurementSystem === 'Imperial') {
            return value * 3.28084; // Convert meters to feet
        }
        return value;
    }

    private convertFromDisplayValue(value: number, type: 'dimension'): number {
        if (type === 'dimension' && this.organizationsService.MeasurementSystem === 'Imperial') {
            return value / 3.28084; // Convert feet back to meters
        }
        return value;
    }

    private roundRect(x: number, y: number, width: number, height: number, radius: number) {
        this.ctx.beginPath();
        this.ctx.moveTo(x + radius, y);
        this.ctx.lineTo(x + width - radius, y);
        this.ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
        this.ctx.lineTo(x + width, y + height - radius);
        this.ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
        this.ctx.lineTo(x + radius, y + height);
        this.ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
        this.ctx.lineTo(x, y + radius);
        this.ctx.quadraticCurveTo(x, y, x + radius, y);
        this.ctx.closePath();
    }

    private getHeaderText(): string {
        if (!this.selectedElements || this.selectedElements.length === 0) {
            return '';
        }
        
        if (this.selectedElements.length === 1) {
            // Show class name for single selection
            const element = this.selectedElements[0];
            return `${element.TypeName}`;
        } else {
            // Show count for multiple selection
            const firstType = this.selectedElements[0].TypeName;
            const allSameType = this.selectedElements.every(el => el.TypeName === firstType);
            const itemType = allSameType ? firstType : 'items';
            return `${this.selectedElements.length} ${itemType}`;
        }
    }

    public commitValueFromInput(input: PropertyInput, value: any): void {
        const inputIndex = this.inputs.indexOf(input);
        if (inputIndex === -1) return;

        const prop = this.commonProperties[inputIndex];
        if (prop.inputType.type === 'color') {
            value = value.replace('#', '');
        }

        this.selectedElements.forEach(element => {
            if(prop.setValue){
                prop.setValue(element, value);
            }
            else if (prop.formControlName) {
                this.setFormControlValue(element, prop.formControlName, value);
                
                // If this is a searchable select and we're setting an Id, also update the Name
                if (prop.inputType.type === 'searchable-select' && prop.formControlName.endsWith('Id')) {
                    const nameField = prop.formControlName.slice(0, -2) + 'Name';
                    const searchableInput = input as SearchableSelectInput;
                    const displayValue = searchableInput.currentDisplayValue;
                    if (element.formGroup.contains(nameField) && displayValue) {
                        this.setFormControlValue(element, nameField, displayValue);
                    }
                }
            }
        });

        // Use level designer's validation method
        this.levelDesigner.validate();
        
        this.levelDesigner.saveState();

        this.levelDesigner.redraw();
    }

    public forceUpdate(): void {
        // Update all input values based on current element state
        this.inputs.forEach((input, index) => {
            const prop = this.commonProperties[index];
            const value = this.getCommonValue(prop);
            input.setValue(value);
        });
    }

    public getWidth(): number {
        return this.width;
    }

    private handleAnimation(currentTime: number) {
        if (this.animationStartTime === null) {
            this.animationStartTime = currentTime;
        }

        const elapsed = currentTime - this.animationStartTime;
        const duration = 500; // 500ms animation duration
        
        if (elapsed < duration) {
            this.animationProgress = this.isCollapsed ? 
                1 - (elapsed / duration) : // Collapsing
                elapsed / duration;        // Expanding
            
            // Use easeInOutCubic for smooth animation
            const t = this.animationProgress;
            this.animationProgress = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
            
            requestAnimationFrame(this.handleAnimation.bind(this));
        } else {
            this.animationProgress = this.isCollapsed ? 0 : 1;
            this.animationStartTime = null;
        }
        
        this.levelDesigner.redraw();
    }

    private toggleCollapse() {
        this.isCollapsed = !this.isCollapsed;
        this.animationStartTime = null;
        requestAnimationFrame(this.handleAnimation.bind(this));
    }

    // Add position and dimension control methods
    public setPosition(x: number, y: number) {
        this.x = x;
        this.y = y;
    }

    public setDimensions(width: number, height: number) {
        this.width = width;
        this.headerHeight = height;
    }

    public handleWheel(event: WheelEvent): boolean {
        const rect = this.canvas.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;

        // If not inside the property editor, don't handle the wheel event
        if (!this.isPointInside(x, y)) {
            return false;
        }

        // If collapsed, don't handle wheel events
        if (this.isCollapsed || this.animationProgress === 0) {
            return false;
        }

        // If we have an active input with a dropdown, let it handle the wheel event
        if (this.activeInput) {
            const inputAsAny = this.activeInput as any;
            if (inputAsAny.dropdownVisible) {
                const handled = this.handleInputWheel(event, this.activeInput);
                if (handled) {
                    event.preventDefault();
                    this.levelDesigner.redraw();
                    return true;
                }
            }
        }

        return false;
    }

    private handleInputWheel(event: WheelEvent, input: PropertyInput): boolean {
        const inputAsAny = input as any;
        if (!inputAsAny.dropdownVisible) return false;

        const deltaY = event.deltaY;
        const optionHeight = 25;
        const maxVisibleOptions = 10;
        const scrollableHeight = (inputAsAny.options.length - maxVisibleOptions) * optionHeight;

        // Update scroll offset
        inputAsAny.scrollOffset = Math.max(0, Math.min(
            inputAsAny.scrollOffset + deltaY,
            scrollableHeight
        ));

        return true;
    }

    public handleDoubleClick(x: number, y: number): MouseEventResult | undefined {
        // Check if click is inside the property editor area
        if (this.isPointInside(x, y)) {
            // Double click inside property editor - do nothing by default
            return { cursor: 'default', stopPropagation: true, redraw: false };
        }
        return undefined;
    }
} 
